import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, SubscriptionLike } from 'rxjs';
import { map } from 'rxjs/operators';
import { Config } from '../../../config';
import { Router, RouterEvent } from '@angular/router';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';

import { CommonService } from '../../../services/common.service';
import { Credential } from '../../../model/credential';
import { ApiUser } from '../../../model/user';

export interface CompanyPasswordPolicy {
  passwordMinLength: string | number;
  passwordMaxLength: string | number;
  passwordRequireNumeric: boolean;
  passwordRequireUppercase: boolean;
  passwordRequireLowercase: boolean;
  passwordRequireSpecialChars: boolean;
  passwordAllowedSpecialChars: string;
  passwordNotUseLastNumPasswords: number;
  passwordMinHoursBetweenChange: number;
  passwordExpiryDays: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  loginErrorMessage: string;
  loginSuccessMessage: string;
  passwordChangeErrorMessage: string;
  messageHasCountdown: boolean = false;
  userProfileSet: boolean = false;
  doFullRefreshOnLogout: boolean = false;
  serverTokenReleaseFailedOnLogout: boolean = false;
  loginNamespace: string = '';

  accountAndSecTabs = {
    account: 0,
    security: 1,
  };
  selectedAccountAndSecTab: number = this.accountAndSecTabs.account;

  idleState: string = '';
  timedOut: boolean = false;
  lastPing?: Date = null;
  countdownSafeValue: number = 4;
  idleInSeconds: number;
  timeoutInSeconds: number = 120; // 2mins
  keepaliveInSeconds: number;
  timeoutCountdown: number;
  tokenExpirationInMins: number;

  private tokenRefreshSubscription: SubscriptionLike;
  private keepAliveSubscription: SubscriptionLike;

  constructor(
    private http: HttpClient,
    private router: Router,
    private cService: CommonService,
    private idle: Idle,
    private keepalive: Keepalive,
    private translate: TranslateService
  ) {
    const authenticated = localStorage.getItem(this.cService.localStorageKeys.userAuthenticated);
    authenticated ? this.isAuthenticated(true) : this.isAuthenticated(false);
  }

  private getAccessTokenExpiration(): number {
    const tokenExpiration = localStorage.getItem(this.cService.localStorageKeys.accessTokenExpiration);

    return tokenExpiration ? Number(tokenExpiration) : null;
  }

  private isSessionExpired(): boolean {
    const sessionExpiryTime = this.getAccessTokenExpiration();

    return sessionExpiryTime && this.getTokenExpirationTime({ inMinutes: true }, sessionExpiryTime) < 0;
  }

  isAuthenticated(authenticated?: boolean): boolean {
    let userAuthenticated: boolean = authenticated;
    const presentInStorage = localStorage.getItem(this.cService.localStorageKeys.userAuthenticated);

    if (userAuthenticated == undefined) {
      if (presentInStorage) {
        userAuthenticated = true;
      }
    }

    if (this.isSessionExpired()) {
      userAuthenticated = false;
    }

    if (userAuthenticated) {
      if (!presentInStorage) {
        localStorage.setItem(this.cService.localStorageKeys.userAuthenticated, 'authenticated');
      }
    } else {
      localStorage.removeItem(this.cService.localStorageKeys.userAuthenticated);
    }

    return userAuthenticated;
  }

  login(credentials: Credential): Observable<ApiUser> {
    if (this.loginNamespace) {
      if (credentials.company) {
        if (!credentials.company.startsWith(this.loginNamespace)) {
          credentials.company = this.loginNamespace + credentials.company;
        }
      }
    }

    const body = {
      ...credentials,
      languageCode:
        this.cService.userProfile && this.cService.userProfile.languageCode
          ? this.cService.userProfile.languageCode
          : this.cService.selectedLanguage.code,
    };

    const loginEndpoint = credentials.company ? 'users/login' : 'users/login_with_username_password';

    return this.http.post(Config.apiRoot.uri + loginEndpoint, body).pipe(
      map((response: ApiUser) => {
        this.successfulLoginCallback(response);
        return response;
      })
    );
  }

  ssoLogin(token: string): Observable<ApiUser> {
    return this.http.post(Config.apiRoot.uri + 'users/login/sso', { token }).pipe(
      map((response: ApiUser) => {
        this.successfulLoginCallback(response, 'SSO');
        return response;
      })
    );
  }

  cognitoSsoLogin(authCode: string): Observable<ApiUser> {
    return this.http.get(`${Config.apiRoot.uri}users/login/cognito-sso?code=${authCode}`).pipe(
      map((response: ApiUser) => {
        this.successfulLoginCallback(response, 'SSO');
        return response;
      })
    );
  }

  supervisionSsoLogin(uuid: string): Observable<ApiUser> {
    return this.http.get(`${Config.apiRoot.uri}api/v1/supervision/auth/uuid/${uuid}`).pipe(
      map((response: ApiUser) => {
        this.successfulLoginCallback(response, 'SSO');
        return response;
      })
    );
  }

  v2TokenLogin(token: string): Observable<ApiUser> {
    return this.http.post(Config.apiRoot.uri + '/users/login/login_with_v2_token', { token }).pipe(
      map((response: ApiUser) => {
        this.successfulLoginCallback(response, 'login_with_v2_token');
        return response;
      })
    );
  }

  private successfulLoginCallback(response: ApiUser, loginType: string = '') {
    this.isAuthenticated(true);

    this.cService.setPageHeader('');
    this.determineDefaultStartPage(response.startPage);
    this.setTimeToLiveToStorage(response.tokenTtl);

    console.log(`Logged In Successfully ${loginType}`, response);

    this.setUserProfileAndTokenExpiration(response);
  }

  refreshToken(): Observable<ApiUser> {
    let refreshTokenRequest: Observable<ApiUser> = null;

    if (this.isAuthenticated) {
      refreshTokenRequest = this.http
        .post<ApiUser>(
          Config.apiRoot.uri + 'users/token/refresh',
          {},
          {
            headers: {
              vrmCustom_hideProgress: 'true',
              vrmCustom_requestType: 'tokenRefresh',
            },
          }
        )
        .pipe(
          map((response: ApiUser) => {
            this.setTimeToLiveToStorage(response.tokenTtl);
            console.log('Token Successfully Refreshed', response.tokenTtl);
            return response;
          })
        );
    }

    return refreshTokenRequest;
  }

  private setTimeToLiveToStorage(timeToLive: number) {
    if (this.cService.userProfile) {
      this.cService.userProfile.tokenTtl = timeToLive;
    }

    this.tokenExpirationInMins = Number(this.getTokenExpirationTime({ inMinutes: true }, timeToLive));
    localStorage.setItem(this.cService.localStorageKeys.accessTokenExpiration, timeToLive.toString());
  }

  private getTokenExpirationTime({ inMinutes }, timeToLive: number) {
    let expirationDate = timeToLive;

    if (inMinutes) {
      const momentDateTimeNow = moment();
      const momentExpDate = moment.unix(timeToLive);
      expirationDate = Math.floor(moment.duration(momentExpDate.diff(momentDateTimeNow)).asMinutes());
    }

    return expirationDate;
  }

  releaseApiTokenOnLogout(): Observable<any> {
    // Release token by logging out of the API
    return this.http.post(Config.apiRoot.uri + 'users/logout', {});
  }

  releaseClientCachedTokenOnLogoutAttempt(doNotRedirect?: boolean, callback?: () => void) {
    this.cService.closeCurrentToast();
    localStorage.removeItem(this.cService.localStorageKeys.accessToken);

    if (doNotRedirect) {
      callback();
    } else {
      if (this.cService.userProfile && this.cService.userProfile.isSso) {
        if (this.cService.userProfile.ssoLogoutUrl) {
          location.href = this.cService.userProfile.ssoLogoutUrl;
        } else {
          this.router.navigate(['sso']);
        }
      } else {
        this.navigateToLoginPage();
      }
    }
  }

  navigateToLoginPage() {
    setTimeout(() => {
      if (this.loginNamespace) {
        this.router.navigate([`login/${this.loginNamespace}`]);
      } else if (this.cService.userProfile && this.cService.userProfile.loginNamespace) {
        this.router.navigate([`login/${this.cService.userProfile.loginNamespace}`]);
      } else if (this.cService.passwordResetNameSpace) {
        this.router.navigate([`login/${this.cService.passwordResetNameSpace}`]);
      } else {
        this.router.navigate([`login`]);
      }
    }, 1000);
  }

  /**
   * @clientOnly - set this flag whenever there's no need to hit the server on logout.
   * There's an instance where hitting the server to confirm logout would lead to a loop.
   *
   * E.g. when called in the http.interceptor after a 401. Logout would also return a 401
   * (since there's no valid token to present to the server at the time). For this reason,
   * logout would be called over and over again as long as 401 is returned
   */
  logout(clientOnly?: boolean, preventReload?: boolean) {
    if (!preventReload) {
      this.doFullRefreshOnLogout = true;
    }

    if (this.keepAliveSubscription) {
      this.keepAliveSubscription.unsubscribe();
    }
    if (this.tokenRefreshSubscription) {
      this.tokenRefreshSubscription.unsubscribe();
    }

    this.cService.closeCurrentToast();
    this.clearUserProfileButPreserveSettings();

    if (clientOnly) {
      this.releaseClientCachedTokenOnLogoutAttempt();
    } else {
      this.releaseApiTokenOnLogout().subscribe({
        next: () => {
          this.releaseClientCachedTokenOnLogoutAttempt();
        },

        error: () => {
          this.releaseClientCachedTokenOnLogoutAttempt();
        },
      });
    }
  }

  /**
   * Use ONLY WHEN there's limited time to await a callback.
   * For instance, when logging the user out on browser-tab closed.
   */
  quickLogout(doNotRedirect?: boolean, callback?: () => void) {
    this.releaseClientCachedTokenOnLogoutAttempt(doNotRedirect, callback);
    this.clearUserProfileButPreserveSettings();
  }

  clearUserProfileButPreserveSettings() {
    this.isAuthenticated(false);

    const persistedProfileData: Array<string> = [
      'HqColour',
      'HqImage',
      'color',
      'contactUsUrl',
      'languageCode',
      'companyLogo',
      'logo',
      'mobileLogo',
    ];

    if (this.cService.userProfile) {
      // Clear all profile data except theme and language related
      const profileDataKeys = Object.keys(this.cService.userProfile);
      profileDataKeys.forEach((propertyKey: string) => {
        if (!persistedProfileData.includes(propertyKey)) {
          delete this.cService.userProfile[propertyKey];
        }
      });

      // Store with settings related data preserved
      localStorage.setItem(this.cService.localStorageKeys.userProfile, JSON.stringify(this.cService.userProfile));
    }

    // Clear others
    localStorage.removeItem(this.cService.localStorageKeys.accessTokenExpiration);
    this.clearTemporaryCacheOnLogout();
  }

  private clearTemporaryCacheOnLogout() {
    const temporaryCacheList = [
      this.cService.localStorageKeys.gridFilterCache,
      this.cService.localStorageKeys.gridCustomHighMedLow,
    ];

    if (temporaryCacheList.length) {
      temporaryCacheList.forEach((cacheId: string) => {
        localStorage.removeItem(cacheId);
      });
    }
  }

  resetTimeoutAndIdleState() {
    this.idle.watch();
    this.idleState = '';
    this.timedOut = false;
  }

  getPasswordPolicyForAuthenticatedUser(): Observable<any> {
    return this.http.get(Config.apiRoot.uri + 'company/password_policy').pipe(
      map((response) => {
        return response;
      })
    );
  }

  changeAccountPassword(credential: Credential, newPassword: string): Observable<boolean> {
    return this.http
      .post(Config.apiRoot.uri + 'users/password/update', {
        username: credential.username,
        company: credential.company,
        password: credential.password,
        newPassword: newPassword,
      })
      .pipe(
        map(() => {
          return true;
        })
      );
  }

  requestPasswordRestLink(credentials: Credential): Observable<any> {
    if (this.loginNamespace) {
      if (credentials.company) {
        if (!credentials.company.startsWith(this.loginNamespace)) {
          credentials.company = this.loginNamespace + credentials.company;
        }
      }
    }

    const body = {
      ...credentials,
      languageCode:
        this.cService.userProfile && this.cService.userProfile.languageCode
          ? this.cService.userProfile.languageCode
          : this.cService.selectedLanguage.code,
    };

    let resetPwEndpoint = 'users/reset_password_with_username';

    if (credentials.company) {
      resetPwEndpoint = 'users/reset_password';
    }

    return this.http.post<any>(Config.apiRoot.uri + resetPwEndpoint, body).pipe(
      map((response) => {
        return response;
      })
    );
  }

  validatePasswordResetTokenAndGetPolicy(token: string): Observable<any> {
    return this.http.get(Config.apiRoot.uri + 'users/reset_password/' + token).pipe(
      map((response) => {
        return response;
      })
    );
  }

  resetAccountPassword(token: string, password: string): Observable<any> {
    return this.http.patch(Config.apiRoot.uri + 'users/reset_password/' + token, { password }).pipe(
      map((response) => {
        return response;
      })
    );
  }

  // Reset_password_with_username
  setUserProfileAndTokenExpiration(profile: ApiUser) {
    this.triggerTokenExpirationAndKeepaliveWatch(profile.tokenTtl);

    console.log('Profile - API', profile);
    const companyLogo = profile.company.companyLogo;

    const missingProfileData = {
      color: 'generic-theme',
      logo: `${this.cService.companiesLogoRoot}${companyLogo}.jpg`,
      mobileLogo: `${this.cService.companiesLogoRoot}${companyLogo}-mobile.png`,
      languageCode: this.cService.selectedLanguage ? this.cService.selectedLanguage.code : 'en',
    };
    const tempProfile = Object.assign(missingProfileData, profile);
    this.cService.userProfile = Object.assign(tempProfile, profile.company);
    this.cService.triggerUserProfileReadyObservable(this.cService.userProfile);

    localStorage.setItem(this.cService.localStorageKeys.userProfile, JSON.stringify(this.cService.userProfile));
    console.log('*Full profile data*', this.cService.userProfile);

    this.cService.applyUserProfile();
    this.userProfileSet = true;
  }

  determineDefaultStartPage(startPage: string) {
    if (startPage) {
      // Try and confirm that start-page exists
      for (let i = 0; i < this.cService.navigation.length; i++) {
        const navParent = this.cService.navigation[i];

        if (navParent.url && navParent.url.toLowerCase().includes(startPage)) {
          Config.defaultPage = startPage;
          this.cService.appSettings.defaultPage = startPage;
          break;
        } else {
          const navChildren = navParent.children;
          let existsInChildren: any;

          if (navChildren && navChildren.length > 0) {
            existsInChildren = navChildren.find((child) => child.url && child.url.toLowerCase().includes(startPage));

            if (existsInChildren) {
              Config.defaultPage = startPage;
              this.cService.appSettings.defaultPage = startPage;
              break;
            }
          }
        }
      }

      const sub = this.router.events.subscribe({
        next: (event: RouterEvent) => {
          const navigatedUrlHasPath = event.url.replace(/\//g, '');

          // Note: In case user is already logged in,
          // attempt navigation again as they didn't go through any login / SSO pages.

          // Also, do not navigate except path is empty
          if (startPage && !navigatedUrlHasPath) {
            this.cService.navigateToPage(startPage);
          }

          // We only want to execute once
          sub.unsubscribe();
        },
      });
    }
  }

  switchToSelectedCompany(selectedCompany: string, redirectTo: string | null = null) {
    const currentCompany = this.cService.userProfile.company.toLowerCase();

    if (selectedCompany.toLowerCase() !== currentCompany) {
      this.cService.showToastWithoutDuration(`Switching Distributor Company to: ${selectedCompany.toUpperCase()}`);
      console.log('User attempted to switch Company', selectedCompany);

      this.cService.commonPost(`/distributor/company/${selectedCompany}`, {}, 'distributor-company-switch').subscribe({
        next: (response: ApiUser) => {
          this.successfulLoginCallback(response, 'Company Switch');

          if (
            this.cService.userProfile.distributorPermissions?.permMisDisplayDistributorPage &&
            this.cService.userProfile.startPage
          ) {
            this.router.navigate([redirectTo || this.cService.userProfile.startPage]).then(() => {
              this.cService.reloadAndTryToApplyUpdates(true);
            });
          } else {
            this.cService.reloadAndTryToApplyUpdates(true);
          }
        },
      });
    }
  }

  triggerTokenExpirationAndKeepaliveWatch(timeToLive: number) {
    if (this.isAuthenticated()) {
      /**
       * IMPORTANT
       * When testing, test with minimum of 9mins
       */
      this.tokenExpirationInMins = Number(this.getTokenExpirationTime({ inMinutes: true }, timeToLive));

      const maxIdeKeepaliveInSeconds = this.cService.convertMinToSeconds(
        this.tokenExpirationInMins - this.countdownSafeValue
      );
      this.idleInSeconds = maxIdeKeepaliveInSeconds;
      this.keepaliveInSeconds = maxIdeKeepaliveInSeconds;

      console.log('%c Token EXPIRATION & REFRESH Data', 'color: salmon', {
        tokenExpirationInMins: this.tokenExpirationInMins,
        idleInSeconds: this.idleInSeconds,
        keepaliveInSeconds: this.keepaliveInSeconds,
      });

      if (this.tokenExpirationInMins) {
        this.idle.setIdle(this.idleInSeconds);
        this.idle.setTimeout(this.timeoutInSeconds);
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        this.idle.onIdleStart.subscribe({
          next: () => {
            this.idleState = `System idle`;
            this.openTimeoutWarning();
            console.log('IDLE START Kicked In');
          },
        });

        this.idle.onTimeoutWarning.subscribe({
          next: (countdown: number) => {
            this.timeoutCountdown = countdown;
            this.idleState = `${this.cService.readyTranslations.security.youWillBeSignedOutIn} ${countdown} ${
              this.translate.instant('UNITS').seconds
            }`;
            console.log(this.idleState);
          },
        });
        this.idle.onTimeout.subscribe({
          next: () => {
            this.idleState = 'Timed out!';
            this.timedOut = true;

            this.logout();
          },
        });

        /**
         * In case user isn't idle for long, then ensure token is not expired
         */
        this.idle.onInterrupt.subscribe({
          next: () => {
            this.closeTimeoutWarning();
          },
        });

        /**
         * Keepalive kicks in and refreshes the token,
         * if the user interrupts idle state / the set keepalive interval is reached
         */
        this.keepalive.interval(this.keepaliveInSeconds);
        this.keepAliveSubscription = this.keepalive.onPing.subscribe({
          next: () => {
            this.tokenRefreshSubscription = this.refreshToken().subscribe({
              next: () => {
                this.lastPing = new Date();
                console.log('Token Expiration in Minutes', this.tokenExpirationInMins);
              },
            });
          },
        });

        this.resetTimeoutAndIdleState();
      } else {
        console.error(':: VRM ERROR : Token expiration management failed due to undefined token expiration time');
      }
    }
  }

  openTimeoutWarning() {
    const toastDuration = this.cService.convertSecondsToMilliseconds(this.timeoutInSeconds);
    this.cService.showTimeoutToastFromComponent(toastDuration, this.timeoutInSeconds);
  }

  closeTimeoutWarning() {
    this.cService.closeCurrentToast();
  }

  navigateToDefaultPage(startPage: string, navCallback?: () => void) {
    if (startPage) {
      this.cService.appSettings.defaultPage = startPage;
    }

    this.router.navigate([this.cService.appSettings.defaultPage]).then(() => {
      if (navCallback) {
        navCallback();
      }
    });
  }
}
